import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface UserProfile {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
  fitness_level: string | null;
  primary_goals: string[] | null;
  preferred_workout_types: string[] | null;
  specific_goals: string | null;
  experience_years: number | null;
  height_cm: number | null;
  weight_kg: number | null;
  birth_date: string | null;
  gender: string | null;
  activity_level: string | null;
  work_type: string | null;
  work_type_custom: string | null;
  health_deficiencies: string | null;
  nutrition_restrictions: string | null;
  plate_diameter_cm: number | null;
  onboarding_completed: boolean | null;
  tutorial_completed: boolean | null;
  ai_coach_name: string | null;
  subscription_tier?: string | null;
  subscription_expires_at?: string | null;
  subscription_source?: string | null;
  apple_original_transaction_id?: string | null;
  premium_early_access?: boolean | null;
  created_at: string;
  updated_at: string;
}

export const useProfile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      setLoading(true);
      fetchProfile();
    } else {
      setProfile(null);
      setLoading(false);
    }
  }, [user]);

  const fetchProfile = async () => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      // Ensure specific_goals field is included even if null
      const profileData = data ? { ...data, specific_goals: data.specific_goals ?? null } : null;
      setProfile(profileData);
    } catch (error: any) {
      // If no row exists yet, treat as not onboarded
      if (error?.code === 'PGRST116' || error?.message?.toLowerCase()?.includes('no rows')) {
        setProfile(null);
      } else {
        console.error('Error fetching profile:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return { error: 'No user found' };

    try {
      const payload = { user_id: user.id, ...updates } as any;
      const { error } = await supabase
        .from('profiles')
        .upsert(payload, { onConflict: 'user_id' });

      if (error) throw error;
      
      await fetchProfile(); // Refresh profile data
      return { error: null };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { error } as any;
    }
  };

  const completeOnboarding = async (profileData: Partial<UserProfile>) => {
    return updateProfile({ ...profileData, onboarding_completed: true });
  };

  return {
    profile,
    loading,
    updateProfile,
    completeOnboarding,
    refetch: fetchProfile
  };
};